let telegramSDK = null;
let initData = null;
let rawInitData = '';
let miniApp = null;
let backButton = null;
let hapticFeedback = null;
let backButtonClickOff = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function requestNativeFullscreen(wa) {
  try {
    if (wa && typeof wa.requestFullscreen === 'function') {
      wa.requestFullscreen();
      return;
    }
  } catch (_) {}
  try {
    const wv = window.Telegram?.WebView;
    if (wv && typeof wv.postEvent === 'function') {
      wv.postEvent('web_app_request_fullscreen', () => {}, {});
    }
  } catch (_) {}
}

function nativeLaunchData() {
  try {
    const wa = window.Telegram?.WebApp;
    let raw = typeof wa?.initData === 'string' ? wa.initData : '';
    if (!raw) {
      const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
      raw = hash.get('tgWebAppData') || '';
    }
    if (!raw) {
      const search = new URLSearchParams(window.location.search || '');
      raw = search.get('tgWebAppData') || '';
    }
    if (raw) {
      rawInitData = raw;
      const params = new URLSearchParams(raw);
      const encodedUser = params.get('user');
      if (encodedUser) {
        return {
          user: JSON.parse(encodedUser),
          start_param: params.get('start_param') || undefined,
        };
      }
    }
    if (wa?.initDataUnsafe?.user) return wa.initDataUnsafe;
    return null;
  } catch (_) {
    return null;
  }
}

async function waitForNativeLaunchData(timeoutMs = 1200) {
  const until = Date.now() + timeoutMs;
  do {
    const data = nativeLaunchData();
    if (data?.user) return data;
    await delay(50);
  } while (Date.now() < until);
  return null;
}

function parseUser(user) {
  if (!user) return null;
  const id = user.id;
  const firstName = user.firstName ?? user.first_name ?? '';
  const lastName = user.lastName ?? user.last_name ?? '';
  const username = user.username ?? '';
  return {
    id,
    firstName,
    lastName,
    username,
    languageCode: user.languageCode || user.language_code || 'ru',
    isBot: user.isBot ?? user.is_bot ?? false,
    isPremium: user.isPremium ?? user.is_premium ?? false,
  };
}

export async function initTelegramSDK() {
  // The official Telegram object and signed tgWebAppData are sufficient for
  // authentication. Keeping third-party SDK mounting out of this critical
  // path prevents iOS WebViews from hanging before the first API request.
  try {
    const wa = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    const launchData = await waitForNativeLaunchData(6000);
    if (launchData?.user) {
      try { if (typeof wa.ready === 'function') wa.ready(); } catch (_) {}
      try { if (typeof wa.expand === 'function') wa.expand(); } catch (_) {}
      requestNativeFullscreen(wa);
      initData = launchData;
      miniApp = wa || {};
      backButton = wa?.BackButton || null;
      hapticFeedback = wa?.HapticFeedback || null;
      return { initData, miniApp };
    }
  } catch (_) {}
  return null;
}

export function getTelegramUser() {
  let user = null;
  if (initData && typeof initData.user !== 'undefined') {
    user = initData.user;
  }
  if (!user && telegramSDK && typeof telegramSDK.retrieveLaunchParams === 'function') {
    try {
      const params = telegramSDK.retrieveLaunchParams();
      user = params.initData?.user;
    } catch (_) {}
  }
  if (!user && typeof window !== 'undefined' && window.Telegram?.WebApp?.initDataUnsafe?.user) {
    user = window.Telegram.WebApp.initDataUnsafe.user;
  }
  return parseUser(user);
}

export function getInitData() {
  return initData;
}

export function getRawInitData() {
  if (rawInitData) return rawInitData;
  try {
    const raw = window.Telegram?.WebApp?.initData;
    if (raw && typeof raw === 'string') {
      rawInitData = raw;
      return raw;
    }
  } catch (_) {}
  try {
    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const raw = params.get('tgWebAppData');
    if (raw) {
      rawInitData = raw;
      return raw;
    }
  } catch (_) {}
  try {
    const raw = new URLSearchParams(window.location.search || '').get('tgWebAppData');
    if (raw) {
      rawInitData = raw;
      return raw;
    }
  } catch (_) {}
  return '';
}

export function getStartParam() {
  const get = (val) => (val && typeof val === 'string' ? val.trim() : '') || null;
  if (initData) {
    const sp = initData.startParam ?? initData.start_param;
    if (sp) return get(sp);
  }
  if (typeof window !== 'undefined') {
    const unsafe = window.Telegram?.WebApp?.initDataUnsafe;
    const sp = unsafe?.startParam ?? unsafe?.start_param;
    if (sp) return get(sp);
  }
  if (telegramSDK && typeof telegramSDK.retrieveLaunchParams === 'function') {
    try {
      const params = telegramSDK.retrieveLaunchParams();
      const sp = params?.startParam ?? params?.initData?.start_param ?? params?.start_param;
      if (sp) return get(sp);
    } catch (_) {}
  }
  // Fallback: Telegram puts ?startapp=value into the URL hash as tgWebAppStartParam=value
  if (typeof window !== 'undefined') {
    const hash = window.location.hash?.slice(1) || '';
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      const sp = hashParams.get('tgWebAppStartParam');
      if (sp) return get(sp);
    }
    // Also check query string (some clients append it there)
    const searchParams = new URLSearchParams(window.location.search || '');
    const sp = searchParams.get('tgWebAppStartParam') || searchParams.get('startapp');
    if (sp) return get(sp);
  }
  return null;
}

export function getReferrerIdFromLaunch() {
  const startParam = getStartParam();
  if (startParam && startParam.startsWith('ref_')) {
    const m = startParam.match(/^ref_(\d+)$/);
    if (m) return m[1];
  }
  if (typeof window !== 'undefined') {
    const hash = window.location.hash?.slice(1) || '';
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      const sp = hashParams.get('tgWebAppStartParam');
      if (sp && sp.startsWith('ref_')) {
        const m = sp.match(/^ref_(\d+)$/);
        if (m) return m[1];
      }
    }
    const params = new URLSearchParams(window.location.search || '');
    const ref = params.get('ref');
    if (ref && /^\d+$/.test(ref)) return ref;
  }
  return null;
}

export function getMiniApp() {
  return miniApp;
}

export function triggerHaptic(style = 'light') {
  try {
    if (hapticFeedback && typeof hapticFeedback.impactOccurred === 'function') {
      hapticFeedback.impactOccurred(style);
      return;
    }
    if (typeof window !== 'undefined' && window.Telegram?.WebApp?.HapticFeedback?.impactOccurred) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred(style);
    }
  } catch (_) {}
}

export function showBackButton(onClick) {
  try {
    if (backButton && typeof backButton.show === 'function') {
      if (typeof backButtonClickOff === 'function') {
        backButtonClickOff();
        backButtonClickOff = null;
      }
      if (typeof onClick === 'function') {
        const handler = () => {
          triggerHaptic();
          onClick();
        };
        if (typeof backButton.on === 'function') {
          backButtonClickOff = backButton.on('click', handler);
          if (typeof backButtonClickOff !== 'function') {
            backButtonClickOff = () => backButton.off('click', handler);
          }
        } else if (typeof backButton.onClick === 'function') {
          backButton.onClick(handler);
          backButtonClickOff = () => {
            try {
              if (typeof backButton.offClick === 'function') backButton.offClick(handler);
            } catch (_) {}
          };
        }
      }
      backButton.show();
      return;
    }
    if (typeof window !== 'undefined' && window.Telegram?.WebApp?.BackButton) {
      const bb = window.Telegram.WebApp.BackButton;
      if (typeof onClick === 'function') {
        if (typeof bb.offClick === 'function') bb.offClick();
        bb.onClick(() => {
          triggerHaptic();
          onClick();
        });
      }
      bb.show();
    }
  } catch (_) {}
}

export function openExternalLink(url) {
  if (!url || typeof url !== 'string') return;
  const isCustomScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !url.startsWith('http://') && !url.startsWith('https://');
  const open = (u) => {
    try {
      if (typeof window !== 'undefined' && window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(u);
        return true;
      }
      if (telegramSDK && typeof telegramSDK.openLink === 'function') {
        telegramSDK.openLink(u);
        return true;
      }
      if (telegramSDK?.Browser?.openLink) {
        telegramSDK.Browser.openLink(u);
        return true;
      }
    } catch (_) {}
    return false;
  };
  if (open(url)) return;
  try {
    if (typeof window !== 'undefined') {
      if (isCustomScheme) {
        window.location.href = url;
        return;
      }
      const w = window.open(url, '_blank', 'noopener');
      if (w) return;
      window.location.href = url;
    }
  } catch (_) {
    if (typeof window !== 'undefined') window.location.href = url;
  }
}

export function hideBackButton() {
  try {
    if (backButton && typeof backButton.hide === 'function') {
      if (typeof backButtonClickOff === 'function') {
        backButtonClickOff();
        backButtonClickOff = null;
      }
      backButton.hide();
      return;
    }
    if (typeof window !== 'undefined' && window.Telegram?.WebApp?.BackButton) {
      if (typeof window.Telegram.WebApp.BackButton.offClick === 'function') {
        window.Telegram.WebApp.BackButton.offClick();
      }
      window.Telegram.WebApp.BackButton.hide();
    }
  } catch (_) {}
}

export function closeWebApp() {
  try {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp?.close) {
      window.Telegram.WebApp.close();
      return true;
    }
  } catch (_) {}
  return false;
}

export function isInsideTelegram() {
  if (typeof window === 'undefined') return false;
  return !!(window.Telegram?.WebApp?.initData || window.Telegram?.WebApp?.initDataUnsafe);
}

